<template>
  <div class="d-flex flex-column justify-content-between">
    <div>
      <SectionLayout id="seo_setting" title="Search Result Preview">
        <Toast v-if="['template-index', 'template-product', 'template-collection'].includes(pageType)" type="warn">
          <template v-if="pageType == 'template-product'" #description>
            Please go to <a target="_blank" :href="linkAllProducts" class="text-mono3">Product in Shopify</a> to edit
            SEO info for each product
          </template>
          <template v-else-if="pageType == 'template-collection'" #description>
            Please go to <a target="_blank" :href="linkAllCollections" class="text-mono3">Product > Collection</a> to
            edit SEO info for each collection
          </template>
          <template v-else #description>
            Please go to
            <a target="_blank" :href="linkShopifyPreferences" class="text-mono3">Online Store > Preferences</a> to edit
            SEO info for Home page
          </template>
        </Toast>
        <template v-else>
          <div class="general__seo p-1">
            <p class="paragraph-14-regular text-light-high general__break mb-1">
              {{ title }}
            </p>
            <a target="_blank" :href="pageUri" class="text-primary body-14-16-regular general__break">{{ pageUri }}</a>
            <p class="body-12-regular text-light-high2 general__break mt-1">
              <template v-if="description">{{ description }}</template>
              <template v-else>{Description will be shown here}</template>
            </p>
          </div>

          <div class="general__seperator my-2"></div>

          <p class="body-14-16-medium text-light-high mb-1">
            {{ pageTitle }}
          </p>
          <InputField
            v-model:value="title"
            :class="`${title.length != 0 ? 'mb-2' : 'mb-5'} ${isHighlightGM ? 'gem-meter-highlight__3rd-party' : ''}`"
            name="title"
            disallow-blank
            hide-bolder
            :clear="false"
            :autofocus="true"
            placeholder="Enter your page title..."
            :input-class="`general__page-title body-14-16-regular text-black4 pl-1  ${
              isHighlightGM ? '!border-amber-300' : ''
            }`" />

          <div class="general__item">
            <div class="row justify-content-between general__title mb-1">
              <p class="body-14-16-medium text-light-high">
                Page URL and Handle
                <SettingTooltip title="Page URL Address" />
              </p>
              <p class="typo-p12 typo-regular text-light-disabled">
                {{ countPageUrlLength }}
              </p>
            </div>
            <div class="row">
              <div class="col-7 bg-light2 general__domain p-0">
                <div ref="domainWrapper" class="d-flex align-items-center general__domain-content ml-1">
                  <span
                    ref="domainContent"
                    class="body-14-16-regular text-light-disabled value mr-1"
                    :class="domainOnRight ? 'right' : 'left'">
                    {{ domain }}
                  </span>
                </div>
              </div>
              <InputField
                v-model:value="handle"
                :class="`${title.length != 0 ? 'mb-2' : 'mb-5'}`"
                class="col-5 p-0"
                name="url"
                disallow-blank
                hide-bolder
                :clear="false"
                :placeholder="`Enter ${titlePlaceHolder} title...`"
                :input-class="`general__url body-14-16-regular text-black4`"
                :max-length="limitUrl"
                :error-message="handleError" />
            </div>
          </div>
          <div class="general__item">
            <div class="row justify-content-between general__title mb-1">
              <p class="body-14-16-medium text-light-high">SEO Description</p>
              <p class="typo-p12 typo-regular text-light-disabled">
                {{ countDescriptionLength }}
              </p>
            </div>
            <div
              :class="{
                'gem-meter-highlight__3rd-party': isHighlightGM,
              }"
              class="flex">
              <textarea
                ref="description"
                v-model="description"
                name="description"
                rows="6"
                :maxlength="limitDescription"
                :class="`general__description body-14-16-regular text-black4 isHighlightGM ? 'gem-meter-highlight__3rd-party border' : ''
                } ${isHighlightGM ? '!border-amber-300' : ''}`"></textarea>
            </div>
          </div>
        </template>
      </SectionLayout>

      <SectionLayout
        id="social_sharing"
        title="Social Sharing Image Preview"
        sub-title="(Recommended size: 1200 x 630 px)">
        <div v-if="isDisableSocialImage" class="bg-primary-600 mb-2 py-1">
          <div class="d-flex">
            <div class="col d-flex align-items-center">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12.6232 4.05295C12.8107 3.86639 12.811 3.56309 12.624 3.37609C12.437 3.18909 12.1337 3.18945 11.9472 3.37689L11.5239 3.80213C11.3385 3.9884 11.3389 4.28958 11.5247 4.47541C11.7105 4.66124 12.0117 4.66159 12.198 4.4762L12.6232 4.05295Z"
                  fill="#456BF2" />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M9.75133 11.0362C9.71025 11.0601 9.68498 11.104 9.68498 11.1515V12.2424C9.68498 12.3678 9.63517 12.488 9.54652 12.5767C9.45787 12.6653 9.33763 12.7151 9.21225 12.7151H6.78801C6.66263 12.7151 6.54239 12.6653 6.45374 12.5767C6.36509 12.488 6.31528 12.3678 6.31528 12.2424V11.1515C6.31528 11.104 6.29001 11.0601 6.24893 11.0362C5.20327 10.4287 4.4971 9.2958 4.4971 7.99999C4.4971 7.07093 4.86617 6.17992 5.52311 5.52298C6.18006 4.86603 7.07107 4.49696 8.00013 4.49696C8.92919 4.49696 9.8202 4.86603 10.4771 5.52298C11.1341 6.17992 11.5032 7.07093 11.5032 7.99999C11.5032 9.2958 10.797 10.4287 9.75133 11.0362ZM7.04967 10.3735C7.17288 10.4226 7.26074 10.5378 7.26074 10.6704V11.4697C7.26074 11.6354 7.39505 11.7697 7.56074 11.7697H8.43952C8.60521 11.7697 8.73952 11.6354 8.73952 11.4697V10.6704C8.73952 10.5378 8.82738 10.4226 8.95059 10.3735C9.89474 9.99782 10.5577 9.07456 10.5577 7.99999C10.5577 7.32168 10.2882 6.67115 9.80861 6.19151C9.32897 5.71187 8.67844 5.44242 8.00013 5.44242C7.32182 5.44242 6.67129 5.71187 6.19165 6.19151C5.71201 6.67115 5.44255 7.32168 5.44255 7.99999C5.44255 9.07456 6.10552 9.99782 7.04967 10.3735Z"
                  fill="#456BF2" />
                <path
                  d="M12.9819 7.99999C12.9819 7.73891 13.1936 7.52727 13.4547 7.52727H14.0607C14.3218 7.52727 14.5335 7.73891 14.5335 7.99999C14.5335 8.26107 14.3218 8.47272 14.0607 8.47272H13.4547C13.1936 8.47272 12.9819 8.26107 12.9819 7.99999Z"
                  fill="#456BF2" />
                <path
                  d="M1.4668 7.99999C1.4668 7.73891 1.67844 7.52727 1.93952 7.52727H2.54558C2.80666 7.52727 3.01831 7.73891 3.01831 7.99999C3.01831 8.26107 2.80666 8.47272 2.54558 8.47272H1.93952C1.67844 8.47272 1.4668 8.26107 1.4668 7.99999Z"
                  fill="#456BF2" />
                <path
                  d="M8.00013 1.46666C8.26121 1.46666 8.47286 1.67831 8.47286 1.93939V2.54545C8.47286 2.80653 8.26121 3.01817 8.00013 3.01817C7.73905 3.01817 7.5274 2.80653 7.5274 2.54545V1.93939C7.5274 1.67831 7.73905 1.46666 8.00013 1.46666Z"
                  fill="#456BF2" />
                <path
                  d="M3.37399 4.04994C3.18785 3.86467 3.18697 3.56363 3.37202 3.37728C3.55799 3.19 3.86085 3.18982 4.04704 3.37689L4.47633 3.80819C4.66135 3.99408 4.66047 4.29482 4.47436 4.47962C4.28918 4.66349 3.99027 4.66332 3.80531 4.47922L3.37399 4.04994Z"
                  fill="#456BF2" />
                <path
                  d="M8.77892 13.5879C8.9446 13.5879 9.07892 13.7222 9.07892 13.8879V14.0606C9.07892 14.186 9.02911 14.3062 8.94046 14.3949C8.85181 14.4835 8.73157 14.5333 8.60619 14.5333H7.39407C7.26869 14.5333 7.14845 14.4835 7.0598 14.3949C6.97115 14.3062 6.92134 14.186 6.92134 14.0606V13.8879C6.92134 13.7222 7.05566 13.5879 7.22134 13.5879H8.77892Z"
                  fill="#456BF2" />
              </svg>
              <span class="body-14-regular txt-dark-low-contrast ml-1"
                >Upgrade to unlock Insert and more powerful features.
              </span>
            </div>
            <a
              class="gp-open-new-tab txt-primary-300 body-12-regular col-2 d-flex align-items-center justify-content-end text-right"
              :data-href="pricingUrl"
              target="blank"
              >See plan</a
            >
          </div>
        </div>
        <Toast v-if="['template-index', 'template-product', 'template-collection'].includes(pageType)" type="warn">
          <template v-if="pageType == 'template-product'" #description>
            For Product Page, when you post a link from your online store to social media, the featured image for that
            page is shown. You can edit it in
            <a target="_blank" :href="linkAllProducts" class="text-mono3">Product</a>
          </template>
          <template v-else-if="pageType == 'template-collection'" #description>
            For Collection Page, when you post a link from your online store to social media, the featured image for
            that page is shown. You can edit it in
            <a target="_blank" :href="linkAllCollections" class="text-mono3">Product > Collection</a>
          </template>
          <template v-else #description>
            Please go to
            <a target="_blank" :href="linkShopifyPreferences" class="text-mono3">Online Store > Preferences</a> to edit
            Social Sharing Image for Home Page
          </template>
        </Toast>
        <div v-else>
          <ImageUpload v-model:value="featuredImage" name="image" :is-disable="isDisableSocialImage" />
          <div class="general__seo image-upload p-1">
            <p class="paragraph-14-regular text-light-high general__break mb-1">
              {{ title }}
            </p>
            <a target="_blank" :href="pageUri" class="text-primary body-14-16-regular general__break">{{ pageUri }}</a>
            <p class="body-12-regular text-light-high2 general__break mt-1">
              <template v-if="description">{{ description }}</template>
              <template v-else>{Description will be shown here}</template>
            </p>
          </div>
        </div>
      </SectionLayout>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import InputField from '@/core/components/form/InputField.vue';
import SettingTooltip from './SettingTooltip.vue';
import SectionLayout from './SectionLayout.vue';
import Toast from '@/core/components/base/Toast.vue';
import ImageUpload from './ImageUpload.vue';
import { mapGetters, mapState } from 'vuex';
import { POST } from '@/app/api';
import api from '@/app/endpoints';
import { isTypeTemplate } from '@/app/utils/helpers';
import throttle from '@/app/utils/throttle';
import goToPricing from '@/mixins/goToPricing';
import { checkKeyValid } from '@/app/utils/checkRoleApply';

// const throttleCallCheckValidHandle = throttle((data: any, successCallback: (res: any) => any, errorCallback: (err: any) => any) => {
//   POST(api.checkPageHandle().url, data, undefined, undefined, undefined).then(successCallback, errorCallback);
// }, 500);

/* eslint-disable vue/no-unused-components */
export default defineComponent({
  name: 'General',
  components: {
    InputField,
    SettingTooltip,
    ImageUpload,
    SectionLayout,
    Toast,
  },
  mixins: [goToPricing],
  props: {
    settingUrl: {
      type: Boolean,
      default: false,
    },
    isHighlightGMProp: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    limitUrl: 70,
    limitDescription: 320,
    handleError: '',
    throttleCheckValidHandle: false,
    domainOnRight: false,
    isHighlightGM: false,
  }),
  computed: {
    ...mapState('app', {
      pageInfo: 'pageInfo',
      shopInfo: 'shopInfo',
    }),
    ...mapGetters('app', {
      shopifyDomain: 'shopifyDomain',
      pageType: 'pageType',
    }),
    ...mapGetters('page', {
      defaultForm: 'generalDefaultForm',
      currentForm: 'generalForm',
      blogHandle: 'blogHandle',
    }),
    title: {
      get() {
        return this.currentForm.title;
      },
      set(value: any) {
        (this as any).$store.commit('page/UPDATE_PAGE_SETTING_FORM', {
          tab: 'general',
          field: 'title',
          value,
        });
      },
    },
    handle: {
      get() {
        return this.currentForm.handle;
      },
      set(value: any) {
        (this as any).$store.commit('page/UPDATE_PAGE_SETTING_FORM', {
          tab: 'general',
          field: 'handle',
          value,
        });
        this.handleError = '';

        if (!this.pageInfo || !this.shopInfo) {
          return;
        }

        if (this.throttleCheckValidHandle) {
          (this.throttleCheckValidHandle as any)();
        }
      },
    },
    description: {
      get() {
        return this.currentForm.description;
      },
      set(value: any) {
        (this as any).$store.commit('page/UPDATE_PAGE_SETTING_FORM', {
          tab: 'general',
          field: 'description',
          value,
        });
      },
    },
    featuredImage: {
      get() {
        return this.currentForm.featuredImage;
      },
      set(value: any) {
        (this as any).$store.commit('page/UPDATE_PAGE_SETTING_FORM', {
          tab: 'general',
          field: 'featuredImage',
          value,
        });
      },
    },
    domain() {
      if (!this.pageType) {
        return this.shopifyDomain;
      }

      let urlHandle = '';
      switch (this.pageType) {
        case 'template-page':
          urlHandle = 'pages/';
          break;
        case 'template-article':
          urlHandle = `blogs/${this.blogHandle}/`;
          break;
      }

      return `https://${this.shopifyDomain}/${urlHandle}`;
    },
    pageUri() {
      return `${this.domain}${this.handle}`;
    },
    countPageUrlLength() {
      return `${this.handle.length}/${this.limitUrl}`;
    },
    countDescriptionLength() {
      return `${this.description.length}/${this.limitDescription}`;
    },
    pageTitle() {
      if (this.pageType == 'template-article') {
        return 'Article Title';
      }

      return 'SEO Page Title';
    },
    titlePlaceHolder() {
      if (this.pageType == 'template-page') {
        return 'page';
      } else {
        return 'article';
      }
    },
    isTemplate() {
      return isTypeTemplate(this.pageType);
    },
    isHomeTemplate() {
      return this.pageType == 'template-index';
    },
    isProductTemplate() {
      return this.pageType == 'template-product';
    },
    isCollectionTemplate() {
      return this.pageType == 'template-collection';
    },
    linkShopifyPreferences() {
      return `https://${this.shopifyDomain}/admin/online_store/preferences`;
    },
    linkAllCollections() {
      return `https://${this.shopifyDomain}/admin/collections`;
    },
    linkAllProducts() {
      return `https://${this.shopifyDomain}/admin/products`;
    },
    isDisableSocialImage() {
      if (checkKeyValid('openStarterSocialImage')) return false;
      return this.shopInfo.plan == 'starter';
    },
  },
  watch: {
    handle(value) {
      this.handle = value.replace(/\s+/g, '-');
    },
  },
  created() {
    this.throttleCheckValidHandle = throttle(this.checkValidHandle, 500) as any;
    if (this.isHighlightGMProp) {
      this.isHighlightGM = this.isHighlightGMProp;
      setTimeout(() => {
        this.isHighlightGM = false;
      }, 2500);
    }
  },
  mounted() {
    this.checkDomainLength();
  },
  methods: {
    commitHandleError(isError = false, msg = '') {
      (this as any).$store.commit('page/PAGE_SETTING_IS_ERROR', isError);

      if (isError) {
        this.handleError = msg ? msg : 'Error, please check again';
      } else {
        this.handleError = '';
      }
    },
    async checkValidHandle() {
      const data = {
        shop: this.shopInfo.shopifyDomain,
        pageId: this.pageInfo.page.id,
        pageType: this.pageType,
        pageHandle: this.handle,
      };

      return POST(api.checkPageHandle().url, data, undefined, undefined, undefined).then(
        (res: any) => {
          const jsonData = res.data;
          if (jsonData.status && jsonData.status == 'success') {
            if (jsonData.pageId) {
              if (jsonData.pageId == this.pageInfo.page.id) {
                this.commitHandleError();
              } else {
                this.commitHandleError(true, 'Error, handle is already used');
              }
            } else {
              this.commitHandleError();
            }
          } else {
            this.commitHandleError(true);
          }
        },
        () => {
          this.commitHandleError(true);
        },
      );
    },
    checkDomainLength() {
      if (this.$refs.domainContent && this.$refs.domainWrapper) {
        const contentLength = (this.$refs.domainContent as any).clientWidth;
        const wrapperLength = (this.$refs.domainWrapper as any).clientWidth;
        this.domainOnRight = contentLength >= wrapperLength;
      }
    },
  },
});
</script>

<style lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.general {
  &__warning-box {
    padding: 8px 32px 8px 40px;
    background-color: rgba($accent-red-new, 0.1);
    color: $text-light-high;
    position: relative;

    a {
      color: $newPrimary;

      .icon {
        vertical-align: middle;
      }
    }
  }
  &__warning-icon {
    position: absolute;
    top: 11px;
    left: 16px;
    color: $accent-red-new;
  }

  &__seperator {
    width: 100%;
    height: 1px;
    background-color: $text-dark-high;
  }

  &__preview {
    &::before {
      content: ' ';
      display: block;
      min-height: 100%;
      width: 4px;
      background: #f2f5ff;
    }
    .general__section {
      padding-left: 20px !important;
    }
  }

  &__section {
    width: 100%;
    background: #fff;
  }
  &__item {
    .row {
      margin: 0;
    }
  }
  &__title {
    width: 100%;
  }
  &__description {
    border-radius: 3px;
    border: solid 1px $highlight2;
    resize: none;
    @include trans-all;

    &:hover {
      background-color: $light1;
      border-color: $text-light-low;
    }

    &:focus {
      border-color: $mono1;
    }

    &::placeholder {
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 17px;
    }
  }
  &__page-title {
    padding: 10px 16px;
    border-radius: 3px;
    border: solid 1px $highlight2;
    height: 32px;
  }

  &__url {
    padding: 10px 8px;
    border-radius: 0 3px 3px 0;
    border: solid 1px $highlight2;
    height: 32px;
  }

  &__domain {
    height: 32px;
    border: solid 1px $highlight2;
    border-right: 0;
    border-radius: 3px 0 0 3px;

    &-content {
      width: calc(100% - 1rem);
      height: 100%;
      white-space: nowrap;
      position: relative;
      overflow: hidden;

      .value {
        position: absolute;

        &.left {
          left: 0;
        }
        &.right {
          right: 0;
        }
      }
    }
  }

  &__seo {
    background: $light2;
    border-radius: 3px;
    &.image-upload {
      border-radius: 0 0 3px 3px;
    }
  }

  &__break {
    word-break: break-all;
    max-width: 520px;
    width: 100%;
  }
}
</style>
